import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useQueries } from "@tanstack/react-query";
import { HorizontalCarousel } from "@v2/components/carousel/HorizontalCarousel";
import { mergeStyleLibraries } from "@v2/features/new-shop-onboarding/utils/styleLibrary";
import { themeToStyleLibrary } from "@v2/features/new-shop-onboarding/utils/themeStyleLibrary";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { nextJSProjectUtils } from "@v2/trpc/routes/nextJSProject";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import {
  starterProjectsOptions,
  starterProjectsRouter,
} from "@v2/trpc/routes/starterProjects";
import { stylesRouter } from "@v2/trpc/routes/styles";
import { extractDomainFromEmail } from "@v2/utils/email";
import { motion } from "motion/react";

import { CustomStylesCard } from "./customStylesCard";
import { SitePreviewCard } from "./SitePreviewCard";
import { StarterProjectsModalFooter } from "./StarterProjectsModalFooter";
import { StarterProjectsModalHeader } from "./StarterProjectsModalHeader";
import { ProductTypeCard } from "./StarterProjectsProductTypeCard";

const PREVIEW_STALE_TIME_MS = 5 * 60 * 1000;

const StarterProjectsModalContent = ({
  projectId,
  isMobile,
}: {
  projectId: string;
  isMobile: boolean;
}) => {
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();
  const { mutateAsync: markOnboarded } = projectRouter.update.useMutation();
  const { mutateAsync: applyStarterProject, isPending: isContinueLoading } =
    starterProjectsRouter.initFromStarterProject.useMutation();
  const { mutateAsync: updateStyleLibrary } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation();
  const [isStartFromScratchLoading, setIsStartFromScratchLoading] =
    React.useState(false);

  const {
    mutateAsync: extractStyles,
    isPending: isExtractingStyles,
    error: extractionError,
    reset: resetExtraction,
  } = stylesRouter.extractStyles.useMutation();

  const [pendingUrl, setPendingUrl] = React.useState<string>("");
  const [extractionStartedAt, setExtractionStartedAt] = React.useState<
    number | null
  >(null);

  const { data: projectData } = projectRouter.getV2.useQuery(projectId);
  const project = projectData?.project;

  const brandDomain = extractDomainFromEmail(user?.email ?? "");
  const isEnabled = Boolean(brandDomain);
  const { data: brandData } = projectRouter.getBrandData.useQuery(
    { domain: brandDomain ?? "" },
    { enabled: isEnabled },
  );
  const hasBrandData =
    Boolean(brandData?.colors?.length) || Boolean(brandData?.fonts?.length);

  const { data: metadata, isLoading: isLoadingMetadata } =
    starterProjectsRouter.getStarterProjectsMetadata.useQuery(
      {},
      {
        staleTime: PREVIEW_STALE_TIME_MS,
      },
    );

  const productTypes = metadata?.productTypes;
  const styles = metadata?.styles;

  const htmlQueries = useQueries({
    queries: (productTypes ?? []).map((productType) => {
      return starterProjectsOptions.getStyledPreviewHtmlByProductType.queryOptions(
        { productTypeId: productType.id },
        {
          staleTime: PREVIEW_STALE_TIME_MS,
          enabled: Boolean(productTypes),
        },
      );
    }),
  });

  const [selectedProductTypeId, setSelectedProductTypeId] = React.useState<
    string | null
  >(null);
  const [selectedStyleId, setSelectedStyleId] = React.useState<string | null>(
    null,
  );
  const [customStyleLibrary, setCustomStyleLibrary] =
    React.useState<StyleLibrary | null>(null);
  const [customStyleMethod, setCustomStyleMethod] = React.useState<
    "branch_fetch" | "url_extract" | null
  >(null);
  const hasSelectedProductTypeRef = React.useRef(false);
  const themesSectionRef = React.useRef<HTMLDivElement>(null);
  const [modalOpenedAt] = React.useState(() => Date.now());

  const handleStartFromScratch = async () => {
    setIsStartFromScratchLoading(true);
    try {
      logEvent("starterProjects.startedFromScratch", {
        productTypeId: selectedProductTypeId,
        hadBrandData: hasBrandData,
      });

      const monoStyleLibrary = themeToStyleLibrary("mono");
      await updateStyleLibrary({
        projectId,
        styleLibrary: monoStyleLibrary,
      });
      await markOnboarded({
        projectId,
        project: { hasOnboardedStarterSites: true },
      });
      await projectUtils.getV2.invalidate(projectId);
    } finally {
      setIsStartFromScratchLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedProductTypeId || !selectedStyleIdOrDefault) {
      return;
    }

    const selectedProductTypeName =
      productTypes?.find((pt) => pt.id === selectedProductTypeId)?.name ?? "";
    const selectedStyleName =
      selectedStyleIdOrDefault === "custom"
        ? "Custom"
        : preparedStyles.find((s) => s.id === selectedStyleIdOrDefault)?.name ??
          "";

    logEvent("starterProjects.template.applied", {
      productTypeId: selectedProductTypeId,
      productTypeName: selectedProductTypeName,
      styleId: selectedStyleIdOrDefault,
      styleName: selectedStyleName,
      isCustomStyle: selectedStyleIdOrDefault === "custom",
      customStyleMethod:
        selectedStyleIdOrDefault === "custom" ? customStyleMethod : null,
      modalOpenedAt,
      templateAppliedAt: Date.now(),
    });

    await applyStarterProject({
      projectId,
      productTypeId: selectedProductTypeId,
      styleId: selectedStyleIdOrDefault,
      customStyleLibrary:
        selectedStyleIdOrDefault === "custom" && customStyleLibrary
          ? customStyleLibrary
          : undefined,
    });

    await projectUtils.getV2.invalidate(projectId);
    void nextJSProjectUtils.listPages.invalidate({ projectId });
  };

  const handleProductTypeSelect = (productTypeId: string) => {
    const isFirstSelection = !hasSelectedProductTypeRef.current;
    setSelectedProductTypeId(productTypeId);

    if (isMobile && isFirstSelection) {
      setTimeout(() => {
        themesSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
    }
  };

  const handleStyleSelect = (styleId: string) => {
    setSelectedStyleId(styleId);
  };

  const handleCustomStyleExtracted = (
    styleLibrary: StyleLibrary,
    method: "branch_fetch" | "url_extract",
  ) => {
    setCustomStyleLibrary(styleLibrary);
    setCustomStyleMethod(method);
    setSelectedStyleId("custom");
  };

  const handleResetCustomStyle = () => {
    setCustomStyleLibrary(null);
    setCustomStyleMethod(null);
  };

  const handlePullStyles = async (
    url: string,
    useBrandData: boolean,
    brandColors?: { hex: string; type: string; brightness: number }[],
    brandFonts?: { name: string; type: string }[],
  ) => {
    if (!url.trim()) {
      return;
    }

    const method: "branch_fetch" | "url_extract" = useBrandData
      ? "branch_fetch"
      : "url_extract";

    const startedAt = Date.now();
    setExtractionStartedAt(startedAt);

    logEvent("starterProjects.customStyle.started", {
      method,
      startedAt,
    });

    let processedUrl = url;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      processedUrl = `https://${url}`;
    }

    setPendingUrl(processedUrl);

    try {
      const data = await extractStyles({ url: processedUrl, projectId });

      if (data.styles.styleLibrary) {
        const finalStyleLibrary =
          useBrandData && brandColors && brandFonts
            ? mergeStyleLibraries(
                data.styles.styleLibrary,
                brandColors,
                brandFonts,
              )
            : data.styles.styleLibrary;

        const brandColorsCount = finalStyleLibrary.colors?.brand
          ? Object.keys(finalStyleLibrary.colors.brand).length
          : 0;
        const neutralColorsCount = finalStyleLibrary.colors?.neutrals
          ? Object.keys(finalStyleLibrary.colors.neutrals).length
          : 0;
        const typographyCount = finalStyleLibrary.typography
          ? Object.keys(finalStyleLibrary.typography).length
          : 0;
        const extractedStylesCount =
          brandColorsCount + neutralColorsCount + typographyCount;

        logEvent("starterProjects.customStyle.completed", {
          method,
          startedAt: extractionStartedAt,
          completedAt: Date.now(),
          extractedStylesCount,
        });

        handleCustomStyleExtracted(finalStyleLibrary, method);
      }
    } catch (error_) {
      const errorType = error_ instanceof Error ? error_.name : "UnknownError";
      const errorMessage =
        error_ instanceof Error ? error_.message : String(error_);

      logEvent("starterProjects.customStyle.failed", {
        method,
        startedAt: extractionStartedAt,
        failedAt: Date.now(),
        errorType,
        errorMessage,
      });

      throw error_;
    }
  };

  const handleCancelExtraction = () => {
    resetExtraction();
    handleResetCustomStyle();
    setPendingUrl("");
    setExtractionStartedAt(null);
  };

  const handleRetryExtraction = () => {
    resetExtraction();
    handleResetCustomStyle();
    setPendingUrl("");
    setExtractionStartedAt(null);
  };

  const preparedStyles = React.useMemo(() => {
    return (styles ?? []).map((style) => ({
      ...style,
      fontFamily: style.styleLibrary?.typography?.h1?.name ?? "Inter",
      fontUrl: style.styleLibrary?.typography?.h1?.url ?? null,
      subtleBackgroundColor:
        style.styleLibrary?.colors?.neutrals?.subtle ?? null,
    }));
  }, [styles]);

  const selectedStyleIdOrDefault = selectedStyleId ?? styles?.[0]?.id ?? "";

  const selectedProductType = React.useMemo(() => {
    if (!productTypes || selectedProductTypeId === null) {
      return null;
    }

    return (
      productTypes.find(
        (productType) => productType.id === selectedProductTypeId,
      ) ?? null
    );
  }, [productTypes, selectedProductTypeId]);

  const isFirstProductTypeSelection =
    !hasSelectedProductTypeRef.current && selectedProductType !== null;

  React.useEffect(() => {
    if (selectedProductType !== null) {
      hasSelectedProductTypeRef.current = true;
    }
  }, [selectedProductType]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="hidden md:block">
        <StarterProjectsModalHeader title="Your site starts here — pick a template" />
      </div>

      <div
        className={twMerge(
          "flex-1 min-h-0 flex flex-col items-center pb-14 md:py-8",
          (selectedProductTypeId !== null || isMobile) && "overflow-auto",
        )}
      >
        {isLoadingMetadata ? (
          <div className="flex items-center justify-center flex-1">
            <Spinner className="size-8" variant="primary" />
          </div>
        ) : (
          <motion.div
            className={twMerge(
              "w-full flex flex-col items-center",
              selectedProductTypeId === null && "justify-center flex-1",
            )}
            layout
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <motion.div
              className="flex flex-col gap-5 w-full md:w-auto items-center md:px-12"
              layout
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <h1 className="text-default font-semibold text-lg">
                What kind of product do you sell?
              </h1>
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full md:w-auto md:justify-center">
                {(productTypes ?? []).map((productType) => (
                  <ProductTypeCard
                    key={productType.id}
                    name={productType.name}
                    description={productType.description}
                    icon={productType.icon}
                    isSelected={selectedProductTypeId === productType.id}
                    onClick={() => handleProductTypeSelect(productType.id)}
                  />
                ))}
              </div>
            </motion.div>

            <div
              ref={themesSectionRef}
              className="w-full mt-6 md:mt-12 relative flex flex-col items-center overflow-visible md:px-10"
            >
              <motion.h1
                initial={false}
                animate={{
                  opacity: selectedProductTypeId !== null ? 1 : 0,
                  transition: {
                    duration: 0.15,
                    delay: isFirstProductTypeSelection ? 0.4 : 0,
                  },
                }}
                className="text-default font-semibold text-lg mb-5"
              >
                Select your theme
              </motion.h1>
              {(productTypes ?? []).map((productType, productTypeIndex) => {
                const isSelected = selectedProductTypeId === productType.id;
                const isInitialSelection =
                  isSelected && isFirstProductTypeSelection;

                if (isMobile && !isSelected) {
                  return null;
                }

                const htmlQuery = htmlQueries[productTypeIndex];
                const isLoadingHtml = htmlQuery?.isLoading ?? true;
                const styledHtmlByStyle =
                  htmlQuery?.data?.styledHtmlByStyle ?? {};
                const pageRoutes = htmlQuery?.data?.pageRoutes ?? [];

                return (
                  <motion.div
                    key={productType.id}
                    initial={false}
                    animate={{
                      opacity: isSelected ? 1 : 0,
                      transition: {
                        duration: isInitialSelection ? 0.15 : 0,
                        delay: isInitialSelection ? 0.4 : 0,
                      },
                    }}
                    className={twMerge(
                      "pt-3 -mt-3 pb-12 w-full overflow-visible",
                      isSelected
                        ? "relative pointer-events-auto [content-visibility:visible]"
                        : "absolute inset-x-0 top-8 pointer-events-none [content-visibility:hidden]",
                    )}
                  >
                    {(() => {
                      const customStyleCard = (
                        <CustomStylesCard
                          key="custom-styles-card"
                          pages={pageRoutes}
                          pageHtmlByRoute={
                            styledHtmlByStyle[styles?.[0]?.id ?? ""] ?? {}
                          }
                          isSelected={selectedStyleIdOrDefault === "custom"}
                          onSelect={() => handleStyleSelect("custom")}
                          brandDomain={brandDomain}
                          hasBrandData={hasBrandData}
                          logoUrl={project?.logoUrl ?? null}
                          extractedStyleLibrary={customStyleLibrary}
                          isPending={isExtractingStyles}
                          hasError={Boolean(extractionError)}
                          pendingUrl={pendingUrl}
                          onPullStyles={handlePullStyles}
                          onCancel={handleCancelExtraction}
                          onRetry={handleRetryExtraction}
                        />
                      );

                      const styleCards = preparedStyles.map((style) => {
                        const isActiveStyle =
                          selectedStyleIdOrDefault === style.id;
                        const pageHtmlByRoute =
                          styledHtmlByStyle[style.id] ?? {};

                        return (
                          <SitePreviewCard
                            key={`${productType.id}-${style.id}`}
                            styleId={style.id}
                            styleName={style.name}
                            fontUrl={style.fontUrl}
                            fontFamily={style.fontFamily}
                            subtleBackgroundColor={style.subtleBackgroundColor}
                            pages={pageRoutes}
                            pageHtmlByRoute={pageHtmlByRoute}
                            isSelected={isActiveStyle}
                            isLoading={isLoadingHtml}
                            onSelect={() => handleStyleSelect(style.id)}
                          />
                        );
                      });

                      const cards = hasBrandData
                        ? [customStyleCard, ...styleCards]
                        : [...styleCards, customStyleCard];

                      return isMobile ? (
                        <div className="flex flex-col gap-3 px-4 w-full">
                          {cards}
                        </div>
                      ) : (
                        <HorizontalCarousel
                          gap="4"
                          listenerDelay={isInitialSelection ? 400 : 0}
                          UNSAFE_className="px-2 py-3 w-fit mx-auto max-w-full"
                        >
                          {cards}
                        </HorizontalCarousel>
                      );
                    })()}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>

      <StarterProjectsModalFooter
        onStartFromScratch={() => void handleStartFromScratch()}
        onContinue={() => void handleContinue()}
        isContinueDisabled={
          !selectedProductTypeId ||
          !selectedStyleIdOrDefault ||
          (selectedStyleIdOrDefault === "custom" && !customStyleLibrary)
        }
        isStartFromScratchLoading={isStartFromScratchLoading}
        isContinueLoading={isContinueLoading}
      />
    </div>
  );
};

interface StarterProjectsModalProps {
  projectId: string;
}

export default function StarterProjectsModal({
  projectId,
}: StarterProjectsModalProps) {
  const isMobile = useIsMobile();

  return isMobile ? (
    <Drawer
      open
      title={
        <StarterProjectsModalHeader title="Your site starts here — pick a template" />
      }
      showDragHandle={false}
      dismissable={false}
    >
      <StarterProjectsModalContent projectId={projectId} isMobile={isMobile} />
    </Drawer>
  ) : (
    <Modal
      open
      dismissable={false}
      showCloseButton={false}
      className="w-full"
      UNSAFE_className="p-0 sm:max-w-[min(1500px,calc(100vw-48px))] h-[min(950px,calc(100vh-48px))] overflow-hidden grid-rows-[1fr]"
    >
      <StarterProjectsModalContent projectId={projectId} isMobile={isMobile} />
    </Modal>
  );
}
